<script setup>
import { ref, computed, useSlots, onMounted } from "vue";
import { voiceOverFocus } from "@/utils/voiceoverfocus";

import QToolTip from "@/components/ui/QToolTip.vue";

const props = defineProps({
    /**
     * The icon to display on the left side of the button
     * @values Any string icon class
     */
    iconLeft: {
        type: String,
        default: "",
    },
    /**
     * The icon to display on the right side of the button
     * @values Any string icon class
     */
    iconRight: {
        type: String,
        default: "",
    },
    /**
     * Whether the button is a link
     * @values true, false
     */
    link: {
        type: Boolean,
    },
    /**
     * Whether the button has no padding
     * @values true, false
     */
    noPad: {
        type: Boolean,
    },
    /**
     * Whether the button is rounded
     * @values true, false
     */
    rounded: {
        type: Boolean,
    },
    /**
     * Whether the button is disabled
     * @values true, false
     */
    disabled: {
        type: Boolean,
    },
    /**
     * Whether the button is full width
     * @values true, false
     */
    full: {
        type: Boolean,
    },
    /**
     * Whether the button is centered
     * @values true, false
     */
    center: {
        type: Boolean,
    },
    /**
     * Whether the button has no hover effect
     * @values true, false
     */
    noHover: {
        type: Boolean,
    },
    /**
     * Whether the button is fat
     * @values true, false
     */
    fat: {
        type: Boolean,
    },
    /**
     * Whether the button is dark
     * @values true, false
     */
    dark: {
        type: Boolean,
    },
    /**
     * Whether the button has a border-radius
     * @values true, false
     */
    radius: {
        type: Boolean,
    },
    /**
     * The aria label for the button
     * @values Any string
     */
    aria: {
        type: String,
        default: null,
    },
    /**
     * Whether the button has voice over focus enabled
     * @values true, false
     */
    voiceOverFocusEnabled: {
        type: Boolean,
    },
    /**
     * The theme of the button
     * @values "primary", "secondary"
     */
    theme: {
        type: String,
        default: "",
    },
    /**
     * Removes button styling completely
     * @values true, false
     */
    noBtn: {
        type: Boolean,
        default: false,
    },
    type: {
        type: String,
        default: "button",
    },
});

const slots = useSlots();

const voiceOverTrigger = ref(null);

const classes = computed(() => {
    return {
        "qmodii-button-icon-only": slots["default"] === undefined ? true : false,
        "qmodii-button-link": props["link"],
        "qmodii-button-nopad": props["noPad"],
        "qmodii-button-radius": props["radius"],
        "qmodii-button-rounded": props["rounded"],
        "qmodii-button-full": props["full"],
        "qmodii-button-center": props["center"],
        "qmodii-button-nohover": props["noHover"],
        "qmodii-button-fat": props["fat"],
        "qmodii-button-dark": props["dark"],
        "qmodii-button-disabled": props["disabled"],
        "qmodii-button-primary": props.theme === "primary",
        "qmodii-button-secondary": props.theme === "secondary",
        "qmodii-button-danger": props.theme === "danger",
        "qmodii-button-no-btn": props["noBtn"],
    };
});

const hasTooltipSlot = computed(() => {
    // Check if the tooltip slot exists and has content
    if (!slots.tooltip) return false;

    // Get the slot content
    const slotContent = slots.tooltip()?.[0];

    return slotContent.children != "v-if";
});

// Function to check if tooltip should be visible
const shouldShowTooltip = computed(() => {
    return tooltipVisible.value && hasTooltipSlot.value;
});

const tooltipVisible = ref(false);

function showTooltip() {
    tooltipVisible.value = true;
}

function hideTooltip() {
    tooltipVisible.value = false;
}

onMounted(async () => {
    if (props.voiceOverFocusEnabled) {
        voiceOverFocus(voiceOverTrigger.value);
    }
});
</script>

<template lang="pug">
button.qmodii-button(
    :class="[classes, props.class]",
    ref="voiceOverTrigger",
    :type="props.type",
    @mouseenter="showTooltip()",
    @mouseleave="hideTooltip()"
)
    i.qmodii-button-icon.qmodii-button-icon-left(v-if="props.iconLeft", :class="props.iconLeft")
    slot.qmodii-icon(v-if="slots['icon-left']", name="icon-left")
    span.qmodii-button-text(
        v-if="slots['default']",
        :aria-label="props.aria ? props.aria : slots['default'].value"
    )
        slot
    .qmodii-button-tooltip(v-if="hasTooltipSlot", ref="tooltip")
        QToolTip(:visible="shouldShowTooltip")
            slot(name="tooltip")
    i.qmodii-button-icon.qmodii-button-icon-right(v-if="props.iconRight", :class="props.iconRight")
</template>

<style lang="stylus">
color-maker(name, inputcolor)
    &.qmodii-button-{name}
        background-color inputcolor
        color #fff
        border-color transparent
        @media (hover: hover) and (pointer: fine)
            &:hover
                background-color lighten(inputcolor, 10%)
                &.qmodii-button-nohover
                  background-color inherit

.qmodii-button
    position relative
    color var(--qmodii-primary-text)
    background-color transparent
    border var(--qmodii-primary-border)
    border-radius var(--qmodii-border-radius-md)
    padding var(--qmodii-button-padding)
    cursor pointer
    display flex
    align-items center
    font-size inherit
    font-family inherit
    text-transform inherit
    font-weight inherit
    white-space nowrap
    gap var(--qmodii-spacing-xs)

    &.qmodii-button-active
        color #fff

    + .qmodii-button
        margin-left var(--qmodii-spacing-xs)

    @media (hover: hover) and (pointer: fine)
        &:hover
            background-color lighten($p-grey, 80%)
            &.qmodii-button-nohover
                background-color inherit

    color-maker('dark', $dark)

    &.qmodii-button-nopad
        padding 0

    &.qmodii-button-link
        border 1px solid transparent

    &:is(button).qmodii-button-icon-only
        justify-content center
        border 0
        background-color transparent
        padding var(--qmodii-spacing-sm)

        @media (hover: hover) and (pointer: fine)
          &:hover
              background-color lighten($p-grey, 80%)
              &.qmodii-button-nohover
                background-color inherit

    &.qmodii-button-radius
        border-radius 5px

    &.qmodii-button-rounded
        border-radius 25px

    &.qmodii-button-full
        width 100%

    &.qmodii-button-center
        justify-content center

    &.qmodii-button-fat
        min-height 50px

    &.qmodii-button-disabled
        pointer-events none
        opacity 0.5

    .qmodii-button-icon
        font-size var(--qmodii-icon-size)

    &.qmodii-button-primary
        color #fff
        background-color var(--qmodii-primary)
        border-color transparent
        padding 6px 12px
        font-weight bold

        @media (hover: hover) and (pointer: fine)
          &:hover
              background-color darken($p-t-c, 20%)
              &.qmodii-button-nohover
                background-color inherit

    &.qmodii-button-secondary
        color var(--qmodii-primary-text)
        background-color var(--qmodii-secondary-button-color)
        border-color transparent

        @media (hover: hover) and (pointer: fine)
          &:hover
              background-color lighten($p-grey, 80%)
              &.qmodii-button-nohover
                background-color inherit

    &.qmodii-button-danger
        color var(--qmodii-error-text)
        background-color var(--qmodii-error-background)
        border-color transparent
        font-weight bold

        @media (hover: hover) and (pointer: fine)
          &:hover
              background-color lighten($p-grey, 80%)
              &.qmodii-button-nohover
                background-color inherit

    &.qmodii-button-no-btn
        border 0
        background-color transparent !important
        color var(--qmodii-primary-text)
        padding 0
        min-height 0

        @media (hover: hover) and (pointer: fine)
          &:hover
              background-color transparent !important
              color var(--qmodii-primary-text)
              &.qmodii-button-nohover
                background-color inherit
                color inherit

    .qmodii-button-tooltip
        position absolute
        bottom 100%
        max-width 150px
        width max-content

        .qmodii-tooltip
            width 100%
</style>
